import { SearchApiParams } from "../types/search";

type FocusParams = Pick<
  SearchApiParams,
  "search" | "search_type" | "search_value" | "focus_place_ids" | "focus_search"
>;

/** Place ids held above the results, parsed from the pipe-separated param. */
export function parseFocusPlaceIds(focusPlaceIds?: string): string[] {
  return (focusPlaceIds ?? "").split("|").filter(Boolean);
}

/**
 * A focus belongs to the venue that created it; any other search replaces it.
 * Selecting from auto-suggest leaves `search` empty and re-running the search
 * fills it back in with the same name, so neither counts as a change.
 *
 * Brands, business types, areas and the nearby pill search by `search_value`
 * rather than by term. The nearby reload behind the focused card is the one
 * search that carries no value of its own, so it keeps the focus.
 */
export function shouldClearFocus(params: FocusParams): boolean {
  if (!params.focus_place_ids) {
    return false;
  }

  if (params.search_value) {
    return true;
  }

  const search = params.search ?? "";
  if (search === "") {
    return false;
  }

  return search !== (params.focus_search ?? "");
}

/**
 * The focused venue's name stays in the search box so the user can see what
 * they picked, but sending it as the text term would narrow the nearby results
 * to that one venue. Only true while the box still holds that name.
 */
export function isFocusDisplayTerm(
  term: string,
  params: Pick<FocusParams, "focus_place_ids" | "focus_search">,
): boolean {
  if (!params.focus_place_ids) {
    return false;
  }

  const trimmed = term.trim();

  return trimmed !== "" && trimmed === (params.focus_search ?? "").trim();
}
